import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect";
import "flatpickr/dist/plugins/monthSelect/style.css";
import apiurl from "../Api";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const THEME_COLOR = "#10296f";

const formatMonthYearLabel = (date) => {
  const monthName = MONTH_NAMES[date.getMonth()];
  const year = date.getFullYear();
  return `${monthName}-${year}`;
};

const formatCurrency = (value) => `₹ ${Number(value || 0).toLocaleString("en-IN")}`;
const normalizeName = (value) => String(value || "").trim().toLowerCase().replace(/\s+/g, " ");

const fetchPayrollData = async (monthYear) => {
  const form = new URLSearchParams();
  form.append("personalMonth", String(monthYear || "").toUpperCase());

  const response = await fetch(`${apiurl}fdashboard.php`, {
    method: "POST",
    body: form,
  });

  if (!response.ok) {
    throw new Error("Failed to fetch payroll data");
  }

  const raw = await response.json();
  const inner = raw?.lavesh ?? raw ?? {};

  return {
    code: String(inner.code ?? ""),
    message: inner.message ?? null,
    status: Boolean(inner.status),
    savedID: Number(inner.savedID ?? 0),
    dataObject: Array.isArray(inner.dataObject) ? inner.dataObject : [],
    url: typeof raw?.url === "string" ? raw.url : null,
    working: typeof raw?.working === "string" ? raw.working : null,
  };
};

const calculateServiceTypeSummary = (data) =>
  Object.values(
    (data || []).reduce((acc, record) => {
      const key = record["Service Type"] || "N/A";
      if (!acc[key]) {
        acc[key] = {
          "Service Type": key,
          "Head Count": 0,
          "Total GP": 0,
          "Total Revenue": 0,
        };
      }
      acc[key]["Head Count"] += Number(record["Head Count"] || 0);
      acc[key]["Total GP"] += Number(record["GP Recurring"] || 0) + Number(record["GP One Time"] || 0);
      acc[key]["Total Revenue"] += Number(record.Revenue || 0);
      return acc;
    }, {})
  );

const calculateLeadSummary = (data) =>
  Object.values(
    (data || []).reduce((acc, record) => {
      const key = record["Contract Engagement Lead"] || "N/A";
      if (!acc[key]) {
        acc[key] = {
          Lead: key,
          "Total Revenue": 0,
          "Head Count": 0,
          "Total GP": 0,
        };
      }
      acc[key]["Total Revenue"] += Number(record.Revenue || 0);
      acc[key]["Head Count"] += Number(record["Head Count"] || 0);
      acc[key]["Total GP"] += Number(record["GP Recurring"] || 0) + Number(record["GP One Time"] || 0);
      return acc;
    }, {})
  );

const calculateCostCenterSummary = (data) =>
  Object.values(
    (data || []).reduce((acc, record) => {
      const key = record["Client Cost Center"] || "N/A";
      if (!acc[key]) {
        acc[key] = {
          "Cost Center": key,
          "Total Revenue": 0,
          "Total Head Count": 0,
          "Total GP": 0,
        };
      }
      acc[key]["Total Revenue"] += Number(record.Revenue || 0);
      acc[key]["Total Head Count"] += Number(record["Head Count"] || 0);
      acc[key]["Total GP"] += Number(record["GP Recurring"] || 0) + Number(record["GP One Time"] || 0);
      return acc;
    }, {})
  );

const calculateClientGrowth = (currentData, previousData) => {
  const currentGrouped = (currentData || []).reduce((acc, record) => {
    const key = record.Client || "N/A";
    if (!acc[key]) acc[key] = { revenue: 0, headCount: 0, gp: 0 };
    acc[key].revenue += Number(record.Revenue || 0);
    acc[key].headCount += Number(record["Head Count"] || 0);
    acc[key].gp += Number(record["GP Recurring"] || 0) + Number(record["GP One Time"] || 0);
    return acc;
  }, {});

  const previousGrouped = (previousData || []).reduce((acc, record) => {
    const key = record.Client || "N/A";
    if (!acc[key]) acc[key] = { revenue: 0, headCount: 0, gp: 0 };
    acc[key].revenue += Number(record.Revenue || 0);
    acc[key].headCount += Number(record["Head Count"] || 0);
    acc[key].gp += Number(record["GP Recurring"] || 0) + Number(record["GP One Time"] || 0);
    return acc;
  }, {});

  return Object.keys(currentGrouped).map((client) => {
    const current = currentGrouped[client];
    const previous = previousGrouped[client] || { revenue: 0, headCount: 0, gp: 0 };
    return {
      Client: client,
      "Revenue Growth": current.revenue - previous.revenue,
      "Headcount Growth": current.headCount - previous.headCount,
      "Total GP Growth": current.gp - previous.gp,
    };
  });
};

const calculateClientDeviation = (data) =>
  (data || []).map((record) => ({
    Client: record.Client || "",
    Username: record["Contract Name"] || "",
    Headcount: Number(record["Head Count"] || 0),
    "GP Total": Number(record["GP Recurring"] || 0) + Number(record["GP One Time"] || 0),
    "Payroll Month": record["Payroll Month"] || "",
    Revenue: Number(record.Revenue || 0),
  }));

const calculateStaffingDeviation = (data) =>
  (data || []).map((record) => ({
    Client: record.Client || "",
    "Cost Center": record["Client Cost Center"] || "",
    Username: record["Contract Name"] || "",
    Headcount: Number(record["Head Count"] || 0),
    "Payroll Month": record["Payroll Month"] || "",
    Revenue: Number(record.Revenue || 0),
    Service: record["Service Type"] || "",
  }));

const calculateGrowthMetrics = (currentData, previousData) => {
  const currentTotals = (currentData || []).reduce(
    (acc, record) => ({
      gp: acc.gp + Number(record["GP Recurring"] || 0) + Number(record["GP One Time"] || 0),
      headCount: acc.headCount + Number(record["Head Count"] || 0),
      revenue: acc.revenue + Number(record.Revenue || 0),
    }),
    { gp: 0, headCount: 0, revenue: 0 }
  );

  const previousTotals = (previousData || []).reduce(
    (acc, record) => ({
      gp: acc.gp + Number(record["GP Recurring"] || 0) + Number(record["GP One Time"] || 0),
      headCount: acc.headCount + Number(record["Head Count"] || 0),
      revenue: acc.revenue + Number(record.Revenue || 0),
    }),
    { gp: 0, headCount: 0, revenue: 0 }
  );

  return {
    gpGrowth: previousTotals.gp ? ((currentTotals.gp - previousTotals.gp) / previousTotals.gp) * 100 : 0,
    gpGrowthDiff: currentTotals.gp - previousTotals.gp,
    headCountGrowth: previousTotals.headCount
      ? ((currentTotals.headCount - previousTotals.headCount) / previousTotals.headCount) * 100
      : 0,
    headCountGrowthDiff: currentTotals.headCount - previousTotals.headCount,
    revenueGrowth: previousTotals.revenue
      ? ((currentTotals.revenue - previousTotals.revenue) / previousTotals.revenue) * 100
      : 0,
    revenueGrowthDiff: currentTotals.revenue - previousTotals.revenue,
  };
};

const StatCard = ({ title, value, accent = THEME_COLOR }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="mb-2 flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{title}</span>
      <span className="h-9 w-9 rounded-lg" style={{ background: `${accent}14`, color: accent }}>
        <svg viewBox="0 0 24 24" className="h-9 w-9 p-2" fill="none" stroke="currentColor" strokeWidth="1.7">
          <path d="M5 12h14M12 5v14" />
        </svg>
      </span>
    </div>
    <div className="text-2xl font-bold text-slate-900">{value}</div>
  </div>
);

const GrowthCard = ({ title, subtitle, value, percentage }) => {
  const positive = Number(percentage || 0) >= 0;
  return (
    <div
      className={`rounded-xl border p-4 shadow-sm ${positive ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}
    >
      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">{subtitle}</div>
      <div className={`mt-2 text-xl font-bold ${positive ? "text-emerald-700" : "text-rose-700"}`}>
        {formatCurrency(value)}
      </div>
      <div className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">{title}</div>
      <div className={`mt-2 text-sm font-bold ${positive ? "text-emerald-600" : "text-rose-600"}`}>
        {Number(percentage || 0).toFixed(2)}%
      </div>
    </div>
  );
};

const ClientListPanel = ({ clients, selectedClient, onClientClick, activeOnly, onActiveToggle }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="mb-4 flex items-center justify-between gap-3">
      <div className="text-sm font-semibold text-slate-800">Clients</div>
      <label
        className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-semibold text-white"
        style={{ background: THEME_COLOR }}
      >
        <input type="checkbox" checked={activeOnly} onChange={(e) => onActiveToggle(e.target.checked)} />
        <span>Active</span>
        <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1">
          {clients.length}
        </span>
      </label>
    </div>
    <div className="max-h-[480px] space-y-2 overflow-y-auto pr-1">
      {[{ value: "all", label: "All Clients" }, ...clients.map((client) => ({ value: client, label: client }))].map((item) => {
        const selected = selectedClient === item.value;
        return (
          <button
            key={item.value}
            type="button"
            onClick={() => onClientClick(item.value)}
            className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left transition ${
              selected ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:bg-slate-50"
            }`}
          >
            <span className={`text-sm font-medium ${selected ? "text-blue-700" : "text-slate-800"}`}>{item.label}</span>
            <span
              className={`rounded-full px-2 py-1 text-[11px] font-semibold ${
                selected ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"
              }`}
            >
              {selected ? "Selected" : "Select"}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

const DataTableCard = ({ title, data, columns, themeColor = THEME_COLOR, maxRows = 8 }) => {
  const [sortState, setSortState] = useState(null);

  const sortedData = useMemo(() => {
    if (!sortState) return data || [];
    const rows = [...(data || [])];
    rows.sort((a, b) => {
      const aVal = a?.[sortState.key];
      const bVal = b?.[sortState.key];
      const aNum = Number(aVal);
      const bNum = Number(bVal);
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        return sortState.direction === "asc" ? aNum - bNum : bNum - aNum;
      }
      const aText = String(aVal ?? "");
      const bText = String(bVal ?? "");
      return sortState.direction === "asc" ? aText.localeCompare(bText) : bText.localeCompare(aText);
    });
    return rows;
  }, [data, sortState]);

  const exportCsv = () => {
    const header = columns.map((col) => `"${String(col.label).replace(/"/g, '""')}"`).join(",");
    const rows = (sortedData || []).map((row) =>
      columns
        .map((col) => `"${String(row?.[col.key] ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${title.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const totals = useMemo(() => {
    const result = {};
    columns.forEach((col) => {
      const everyNumber = (sortedData || []).every((row) => {
        const value = row?.[col.key];
        return value !== "" && value !== null && value !== undefined && !Number.isNaN(Number(value));
      });
      result[col.key] = everyNumber
        ? (sortedData || []).reduce((sum, row) => sum + Number(row?.[col.key] || 0), 0)
        : "";
    });
    return result;
  }, [columns, sortedData]);

  const handleSort = (key) => {
    setSortState((prev) => {
      if (!prev || prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return null;
    });
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between px-4 py-3">
        <h3 className="text-base font-semibold text-slate-800">
          {title} <span className="text-sm text-slate-500">({(data || []).length})</span>
        </h3>
        <button
          type="button"
          onClick={exportCsv}
          className="rounded-md px-3 py-1 text-sm font-medium text-white"
          style={{ background: themeColor }}
        >
          Export
        </button>
      </div>
      <div className="overflow-auto px-4 pb-4">
        <div className="max-h-[360px] overflow-auto rounded-lg border border-slate-200">
          <table className="w-full table-fixed">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className="sticky top-0 border-b border-slate-200 px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.08em] text-white"
                    style={{ background: themeColor }}
                  >
                    <button type="button" onClick={() => handleSort(col.key)} className="flex w-full items-center justify-between">
                      <span>{col.label}</span>
                      <span>{sortState?.key === col.key ? (sortState.direction === "asc" ? "↑" : "↓") : "▾"}</span>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(sortedData || []).slice(0, Math.max(maxRows, sortedData.length)).map((row, index) => (
                <tr key={`${title}-${index}`} className={index % 2 ? "bg-slate-50" : "bg-white"}>
                  {columns.map((col) => (
                    <td key={col.key} className="border-b border-slate-100 px-3 py-2 text-sm text-slate-700">
                      {typeof col.format === "function" ? col.format(row?.[col.key], row) : row?.[col.key]}
                    </td>
                  ))}
                </tr>
              ))}
              {sortedData?.length > 0 && (
                <tr>
                  {columns.map((col, idx) => (
                    <td key={col.key} className="sticky bottom-0 px-3 py-2 text-sm font-semibold text-white" style={{ background: themeColor }}>
                      {idx === 0 ? "TOTAL" : typeof totals[col.key] === "number" ? totals[col.key].toLocaleString("en-IN") : ""}
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

function MtdDashboard() {
  const [baseDate, setBaseDate] = useState(() => new Date());
  const [selectedMonth, setSelectedMonth] = useState(() => formatMonthYearLabel(new Date()));
  const [previousMonth, setPreviousMonth] = useState(() => {
    const prev = new Date();
    prev.setMonth(prev.getMonth() - 1);
    return formatMonthYearLabel(prev);
  });
  const [selectedLead, setSelectedLead] = useState("all");
  const [selectedClient, setSelectedClient] = useState("all");
  const [activeOnly, setActiveOnly] = useState(true);
  const [currentPayload, setCurrentPayload] = useState(null);
  const [previousPayload, setPreviousPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [loggedInPersonName, setLoggedInPersonName] = useState("");
  const autoLeadAppliedRef = useRef(false);

  const currentLabel = useMemo(() => formatMonthYearLabel(baseDate), [baseDate]);
  const previousLabel = useMemo(() => {
    const prev = new Date(baseDate);
    prev.setMonth(prev.getMonth() - 1);
    return formatMonthYearLabel(prev);
  }, [baseDate]);

  const loadData = async (month, prevMonth) => {
    setLoading(true);
    setErrorMsg("");
    try {
      const [current, previous] = await Promise.all([fetchPayrollData(month), fetchPayrollData(prevMonth)]);
      setCurrentPayload(current);
      setPreviousPayload(previous);
      if (current?.status === false) {
        setErrorMsg(current?.working || current?.message || "API not working properly");
      }
    } catch (error) {
      setErrorMsg(error?.message || "Failed to load MTD dashboard");
      setCurrentPayload(null);
      setPreviousPayload(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(selectedMonth, previousMonth);
  }, [selectedMonth, previousMonth]);

  useEffect(() => {
    let ignore = false;

    const fetchSessionUser = async () => {
      try {
        const response = await axios.post(`${apiurl}session.php`);
        if (!ignore) {
          setLoggedInPersonName(String(response?.data?.banda_naam || "").trim());
        }
      } catch (error) {
        if (!ignore) {
          setLoggedInPersonName("");
        }
      }
    };

    fetchSessionUser();

    return () => {
      ignore = true;
    };
  }, []);

  const rawData = useMemo(() => currentPayload?.dataObject || [], [currentPayload]);
  const previousRawData = useMemo(() => previousPayload?.dataObject || [], [previousPayload]);
  const leadOptions = useMemo(() => {
    const uniq = Array.from(new Set(rawData.map((row) => row?.["Contract Engagement Lead"]).filter(Boolean)));
    return [{ value: "all", label: "All" }, ...uniq.map((item) => ({ value: item, label: item }))];
  }, [rawData]);

  const filteredData = useMemo(() => {
    return rawData.filter((record) => {
      if (activeOnly && record?.["Client Status"] !== "Active") return false;
      if (selectedLead !== "all" && record?.["Contract Engagement Lead"] !== selectedLead) return false;
      if (selectedClient !== "all" && record?.Client !== selectedClient) return false;
      return true;
    });
  }, [activeOnly, rawData, selectedClient, selectedLead]);

  const stats = useMemo(() => {
    const activeClients = new Set(filteredData.map((item) => item?.Client).filter(Boolean));
    return {
      totalGP: filteredData.reduce(
        (sum, item) => sum + Number(item?.["GP Recurring"] || 0) + Number(item?.["GP One Time"] || 0),
        0
      ),
      totalRevenue: filteredData.reduce((sum, item) => sum + Number(item?.Revenue || 0), 0),
      totalHeadCount: filteredData.reduce((sum, item) => sum + Number(item?.["Head Count"] || 0), 0),
      gpRecurring: filteredData.reduce((sum, item) => sum + Number(item?.["GP Recurring"] || 0), 0),
      gpOneTime: filteredData.reduce((sum, item) => sum + Number(item?.["GP One Time"] || 0), 0),
      activeClients: activeClients.size,
    };
  }, [filteredData]);

  const growthMetrics = useMemo(
    () => (previousRawData.length ? calculateGrowthMetrics(filteredData, previousRawData) : null),
    [filteredData, previousRawData]
  );

  const uniqueLeads = useMemo(() => leadOptions.map((item) => item.value).filter((item) => item !== "all"), [leadOptions]);

  const visibleClients = useMemo(() => {
    const rows = rawData.filter((record) => {
      if (activeOnly && record?.["Client Status"] !== "Active") return false;
      if (selectedLead !== "all" && record?.["Contract Engagement Lead"] !== selectedLead) return false;
      return true;
    });
    return Array.from(new Set(rows.map((record) => record?.Client).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [activeOnly, rawData, selectedLead]);

  const matchedLoggedInLead = useMemo(() => {
    if (!loggedInPersonName || !Array.isArray(uniqueLeads) || uniqueLeads.length === 0) return "";
    const userName = normalizeName(loggedInPersonName);
    return (
      uniqueLeads.find((lead) => normalizeName(lead) === userName) ||
      uniqueLeads.find((lead) => normalizeName(lead).includes(userName)) ||
      uniqueLeads.find((lead) => userName.includes(normalizeName(lead))) ||
      ""
    );
  }, [loggedInPersonName, uniqueLeads]);

  const showLeadNoDataState = useMemo(() => {
    if (!loggedInPersonName) return false;
    if (!rawData.length) return false;
    return !matchedLoggedInLead;
  }, [loggedInPersonName, matchedLoggedInLead, rawData]);

  useEffect(() => {
    if (autoLeadAppliedRef.current) return;
    if (!loggedInPersonName) return;
    if (!Array.isArray(uniqueLeads) || uniqueLeads.length === 0) return;

    if (matchedLoggedInLead) {
      setSelectedLead(matchedLoggedInLead);
      autoLeadAppliedRef.current = true;
    }
  }, [loggedInPersonName, matchedLoggedInLead, uniqueLeads]);

  const handleMonthPicked = (dates) => {
    const picked = dates?.[0];
    if (!picked) return;
    const current = formatMonthYearLabel(picked);
    const prev = new Date(picked);
    prev.setMonth(prev.getMonth() - 1);
    setBaseDate(picked);
    setSelectedMonth(current);
    setPreviousMonth(formatMonthYearLabel(prev));
  };

  const handleMonthToggle = (month) => {
    if (month === selectedMonth) return;
    setPreviousMonth(selectedMonth);
    setSelectedMonth(month);
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-slate-500">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-100 border-t-blue-700" />
          <p className="text-sm font-medium">Loading MTD dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-[1800px] space-y-6">
        {errorMsg ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{errorMsg}</div>
        ) : null}

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex min-w-[220px] flex-col">
              <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Selected Lead</span>
              <span className="mt-2 text-lg font-semibold text-slate-900">{selectedLead === "all" ? "All Leads" : selectedLead}</span>
            </div>

            <div className="ms-auto flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-2 shadow-inner">
              <Flatpickr
                value={baseDate}
                onChange={handleMonthPicked}
                options={{
                  plugins: [
                    monthSelectPlugin({
                      shorthand: true,
                      dateFormat: "F-Y",
                      altFormat: "F-Y",
                    }),
                  ],
                  defaultDate: baseDate,
                }}
                render={(_, ref) => (
                  <button
                    type="button"
                    ref={ref}
                    className="inline-flex h-11 items-center rounded-xl border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-400 hover:bg-slate-100"
                  >
                    <span className="me-2 inline-flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <path d="M8 3.75V6" />
                        <path d="M16 3.75V6" />
                        <path d="M4.75 9.25h14.5" />
                        <rect x="4.75" y="5.25" width="14.5" height="14" rx="2.25" />
                      </svg>
                    </span>
                    Select Month
                  </button>
                )}
              />

              <button
                type="button"
                onClick={() => handleMonthToggle(previousLabel)}
                className={`inline-flex h-11 items-center rounded-xl px-5 text-sm font-semibold shadow-sm transition ${
                  selectedMonth === previousLabel
                    ? "border border-blue-900 bg-blue-900 text-white hover:bg-blue-950"
                    : "border border-blue-200 bg-white text-blue-900 hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50"
                }`}
              >
                {previousLabel}
              </button>

              <button
                type="button"
                onClick={() => handleMonthToggle(currentLabel)}
                className={`inline-flex h-11 items-center rounded-xl px-5 text-sm font-semibold shadow-sm transition ${
                  selectedMonth === currentLabel
                    ? "border border-blue-900 bg-blue-900 text-white hover:bg-blue-950"
                    : "border border-blue-200 bg-white text-blue-900 hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50"
                }`}
              >
                {currentLabel}
              </button>

              <button
                type="button"
                className="inline-flex h-10 items-center rounded-xl border border-cyan-300 bg-white px-3.5 text-sm font-semibold text-cyan-700 shadow-sm transition hover:-translate-y-0.5 hover:border-cyan-400 hover:bg-cyan-50"
                onClick={() => loadData(selectedMonth, previousMonth)}
              >
                Refresh
              </button>
            </div>
          </div>
        </div>

        {showLeadNoDataState ? (
          <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-blue-50 px-6 py-10 shadow-sm">
            <div className="mx-auto flex max-w-2xl flex-col items-center text-center">
              <div
                className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl shadow-sm"
                style={{ background: "linear-gradient(135deg, #10296f 0%, #2563eb 100%)" }}
              >
                <svg viewBox="0 0 24 24" className="h-8 w-8 text-white" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M7 7h10" />
                  <path d="M7 12h6" />
                  <path d="M7 17h4" />
                  <path d="M6 4.5h12A1.5 1.5 0 0 1 19.5 6v12a1.5 1.5 0 0 1-1.5 1.5H6A1.5 1.5 0 0 1 4.5 18V6A1.5 1.5 0 0 1 6 4.5Z" />
                </svg>
              </div>

              <h3 className="text-xl font-semibold text-slate-900">No records available for this selection</h3>
              <p className="mt-3 max-w-xl text-sm leading-6 text-slate-600">
                No client lead records were found for <span className="font-semibold text-slate-800">{loggedInPersonName}</span> in{" "}
                <span className="font-semibold text-slate-800">{selectedMonth}</span>. Please select a different month to view available data.
              </p>

              <div className="mt-5 inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-blue-700">
                MTD Dashboard
              </div>
            </div>
          </div>
        ) : null}

        {!showLeadNoDataState ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-8">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <StatCard title="Total GP" value={formatCurrency(stats.totalGP)} />
              <StatCard title="Total Revenue" value={formatCurrency(stats.totalRevenue)} />
              <StatCard title="Total Headcount" value={Number(stats.totalHeadCount).toLocaleString("en-IN")} />
            </div>

            {growthMetrics ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <GrowthCard
                  subtitle="GP Total Growth Diff"
                  title="GP Growth %"
                  value={growthMetrics.gpGrowthDiff}
                  percentage={growthMetrics.gpGrowth}
                />
                <GrowthCard
                  subtitle="Headcount Diff"
                  title="Head Count Growth %"
                  value={growthMetrics.headCountGrowthDiff}
                  percentage={growthMetrics.headCountGrowth}
                />
                <GrowthCard
                  subtitle="Revenue Growth Diff"
                  title="Revenue Growth %"
                  value={growthMetrics.revenueGrowthDiff}
                  percentage={growthMetrics.revenueGrowth}
                />
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <StatCard title="GP Recurring" value={formatCurrency(stats.gpRecurring)} />
              <StatCard title="GP One Time" value={formatCurrency(stats.gpOneTime)} />
              <StatCard title="Total Active Client" value={Number(stats.activeClients).toLocaleString("en-IN")} />
            </div>
          </div>

          <div className="lg:col-span-4">
            <ClientListPanel
              clients={visibleClients}
              selectedClient={selectedClient}
              onClientClick={setSelectedClient}
              activeOnly={activeOnly}
              onActiveToggle={setActiveOnly}
            />
          </div>
        </div>
        ) : null}

        {!showLeadNoDataState ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <DataTableCard
            title="Service Type"
            data={calculateServiceTypeSummary(filteredData)}
            columns={[
              { key: "Service Type", label: "Service Type" },
              { key: "Head Count", label: "Head Count", format: (value) => Number(value || 0).toLocaleString("en-IN") },
              { key: "Total GP", label: "Total GP", format: (value) => formatCurrency(value) },
              { key: "Total Revenue", label: "Total Revenue", format: (value) => formatCurrency(value) },
            ]}
          />
          <DataTableCard
            title="Lead"
            data={calculateLeadSummary(filteredData)}
            columns={[
              { key: "Lead", label: "Lead" },
              { key: "Total Revenue", label: "Total Revenue", format: (value) => formatCurrency(value) },
              { key: "Head Count", label: "Head Count", format: (value) => Number(value || 0).toLocaleString("en-IN") },
              { key: "Total GP", label: "Total GP", format: (value) => formatCurrency(value) },
            ]}
          />
        </div>
        ) : null}

        {!showLeadNoDataState ? (
        <DataTableCard
          title="Cost Center"
          data={calculateCostCenterSummary(filteredData)}
          columns={[
            { key: "Cost Center", label: "Cost Center" },
            { key: "Total Revenue", label: "Total Revenue", format: (value) => formatCurrency(value) },
            { key: "Total Head Count", label: "Total Head Count", format: (value) => Number(value || 0).toLocaleString("en-IN") },
            { key: "Total GP", label: "Total GP", format: (value) => formatCurrency(value) },
          ]}
        />
        ) : null}

        {!showLeadNoDataState ? (
        <DataTableCard
          title="Client Growth"
          data={calculateClientGrowth(filteredData, previousRawData)}
          columns={[
            { key: "Client", label: "Client" },
            { key: "Revenue Growth", label: "Revenue Growth", format: (value) => formatCurrency(value) },
            { key: "Headcount Growth", label: "Headcount Growth", format: (value) => Number(value || 0).toLocaleString("en-IN") },
            { key: "Total GP Growth", label: "Total GP Growth", format: (value) => formatCurrency(value) },
          ]}
        />
        ) : null}

        {!showLeadNoDataState ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <DataTableCard
            title="Client Deviation"
            data={calculateClientDeviation(filteredData)}
            columns={[
              { key: "Client", label: "Client" },
              { key: "Username", label: "Username" },
              { key: "Headcount", label: "Headcount", format: (value) => Number(value || 0).toLocaleString("en-IN") },
              { key: "GP Total", label: "GP Total", format: (value) => formatCurrency(value) },
              { key: "Payroll Month", label: "Payroll Month" },
              { key: "Revenue", label: "Revenue", format: (value) => formatCurrency(value) },
            ]}
          />

          <DataTableCard
            title="Staffing Deviation"
            data={calculateStaffingDeviation(filteredData)}
            columns={[
              { key: "Client", label: "Client" },
              { key: "Cost Center", label: "Cost Center" },
              { key: "Username", label: "Username" },
              { key: "Headcount", label: "Headcount", format: (value) => Number(value || 0).toLocaleString("en-IN") },
              { key: "Payroll Month", label: "Payroll Month" },
              { key: "Revenue", label: "Revenue", format: (value) => formatCurrency(value) },
              { key: "Service", label: "Service" },
            ]}
          />
        </div>
        ) : null}
      </div>
    </div>
  );
}

export default MtdDashboard;
